import { useEffect, useState, memo } from "react";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import axiosInstance from "../utils/axiosInstance";
import { getRecaptchaToken } from "../utils/getcapthcatoken";
import { postCommentForm } from "./http/commonApi";

const initailObject = {
  name: "",
  email: "",
  comment: "",
};

const PostComments = memo(({ id, className = "" }) => {
  const { t } = useTranslation("blogs");
  const router = useRouter();
  const lang = router.locale || "en";

  const [formValues, setFormValues] = useState(initailObject);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formToken, setFormToken] = useState("");
  const [startedAt] = useState(() => Date.now());
  const [websiteTrap, setWebsiteTrap] = useState("");

  useEffect(() => {
    const loadToken = async () => {
      try {
        const res = await axiosInstance.get(`/form-token?lang=${lang}`);
        setFormToken(res?.data?.formToken || "");
      } catch (err) {
        console.error("Failed to load form token", err);
        setFormToken("");
      }
    };

    loadToken();
  }, [lang]);

  const handleInputChange = (e) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });

    setErrors({ ...errors, [e.target.name]: "" });
  };

  const PostCommentFormData = async (payload) => {
    try {
      const response = await postCommentForm(id, payload);

      if (response.status === 200 || response.status === 201) {
        toast.success(response.data?.message || "Comment added Successfully!");
        setFormValues({ ...initailObject });
        setErrors({});

        try {
          const res = await axiosInstance.get(`/form-token?lang=${lang}`);
          setFormToken(res?.data?.formToken || "");
        } catch {
          setFormToken("");
        }
      }
    } catch (error) {
      console.error("Error posting Data:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something Went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!formToken) {
      toast.error("Please wait a moment and try again.");
      return;
    }

    const { name, email, comment } = formValues;
    const nextErrors = {};

    if (!name.trim()) {
      nextErrors.name = "Please Enter Name.";
    }

    if (!email.trim()) {
      nextErrors.email = "Please Enter Email.";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email.trim())) {
      nextErrors.email = "Invalid email address.";
    }

    if (!comment.trim()) {
      nextErrors.comment = "Please Enter Comments.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setLoading(true);

    try {
      const recaptchaToken = await getRecaptchaToken("blog_comment_submit");
      const timeOnPageMs = Date.now() - startedAt;

      const payload = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        comment: comment.trim(),

        formToken,
        timeOnPageMs,
        website: websiteTrap,
        recaptchaToken,
      };

      await PostCommentFormData(payload);
    } catch (error) {
      console.error("Blog comment protection error:", error);
      toast.error("Something went wrong. Please try again later.");
      setLoading(false);
    }
  };

  return (
    <section
      className={`self-stretch bg-text-primary overflow-hidden flex flex-row items-start justify-start py-[40px] lg:py-[60px] px-6 sm:px-6 lg:px-20 box-border max-w-full text-45xl text-background-color-primary ${className}`}
    >
      <div className="flex-1 flex flex-col lg:flex-row items-start justify-start gap-[60px] max-w-[1600px] mx-auto">
        <h2 className="m-0 w-full lg:w-[496px] relative text-[28px] sm:text-[32px] lg:text-[40px] leading-[120%] font-medium font-[inherit] inline-block shrink-0 max-w-full">
          {t("blogs.comment.title")}
        </h2>

        <form
          className="flex-1 flex flex-col items-start justify-start gap-6 w-full box-border text-base"
          onSubmit={handleSubmit}
        >
          <div className="self-stretch flex flex-col items-start justify-start gap-8 max-w-full w-full">
            <div className="w-full flex flex-col gap-2">
              <input
                type="text"
                id="name"
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                className="text-[16px] border-b w-full border-gray-300 bg-transparent text-background-color-primary outline-none pb-2 placeholder:text-white"
                placeholder={t("blogs.comment.form.name")}
                autoComplete="name"
              />
              {errors?.name && (
                <p className="m-0 text-[14px]" style={{ color: "red" }}>
                  {errors.name}
                </p>
              )}
            </div>

            <div className="w-full flex flex-col gap-2">
              <input
                type="email"
                id="email"
                name="email"
                value={formValues.email}
                onChange={handleInputChange}
                className="text-[16px] border-b w-full border-gray-300 bg-transparent text-background-color-primary outline-none pb-2 placeholder:text-white"
                placeholder={t("blogs.comment.form.email")}
                autoComplete="email"
              />
              {errors?.email && (
                <p className="m-0 text-[14px]" style={{ color: "red" }}>
                  {errors.email}
                </p>
              )}
            </div>

            <div className="w-full flex flex-col gap-2">
              <textarea
                id="comment"
                name="comment"
                value={formValues.comment}
                onChange={handleInputChange}
                rows={4}
                className="text-[16px] border-b border-gray-300 bg-transparent text-background-color-primary outline-none pb-2 w-full resize-none placeholder:text-white"
                placeholder={t("blogs.comment.form.comment")}
              />
              {errors?.comment && (
                <p className="m-0 text-[14px]" style={{ color: "red" }}>
                  {errors.comment}
                </p>
              )}
            </div>
          </div>

          <input
            type="text"
            name="website"
            value={websiteTrap}
            onChange={(e) => setWebsiteTrap(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            style={{
              position: "absolute",
              left: "-10000px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          />

          <button
            type="submit"
            className="rounded-[50px] bg-crimson text-white text-[16px] font-medium py-3 px-6 cursor-pointer hover:bg-[#e72c46] transition duration-300 ease-in-out disabled:cursor-not-allowed disabled:opacity-70 blog_comments_form"
            disabled={loading || !formToken}
          >
            {loading ? "Sending..." : t("blogs.comment.form.btnTxt")}
          </button>
        </form>
      </div>
    </section>
  );
});

PostComments.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default PostComments;
